import { AggregateRequestType } from 'common/models/page-composer.model';
import { WidgetRequestWithFilters } from '../models/widget.model';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomFilter, CustomFilterAttribute } from '../models/page-config.models';
import {
  ChartFilterModel,
  CommonFiltersItem,
  FilterConfig,
  FilterParent,
  FilterType,
} from '../models/filter.model';
import {
  ALIGNMENT_DETAIL_FILTER,
  ALIGNMENT_FILTER,
  COACH_FILTER,
} from '../../modules/insights/constants';
import { UNIVERSAL_FILTER } from 'modules/insights/constants/constants-universal-filters';
import { INSIGHT_SUMMARY_FILTER } from 'modules/insights/constants/constants-insights-filters';
import { INTERACTION_FILTER } from 'modules/insights/constants/constants-interaction-filters';
import { MIR_FILTER } from 'modules/insights/constants/constants-mir-filters';
import { getAttributeKey } from 'common/utils/filter.utils';
import { useSelector } from 'react-redux';
import { RootState } from 'common/store';
import { selectChartFilters } from 'common/store/slices/chart-filters.slice';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { convertToKwelloDate } from '../constants';

export interface TransformCommonFiltersForRequestObject {
  [key: string]: Array<string | number> | { [key: string]: Array<string | number> };
}

export function transformCommonFiltersForRequest(
  filters: CommonFiltersItem
): TransformCommonFiltersForRequestObject {
  const results: any = {};
  Object.keys(filters)
    .filter((key: string) => {
      return !key.includes('attributes_');
    })
    .forEach((key: string) => {
      const iterationFilters = filters[key];
      results[key + 'Ids'] = iterationFilters.value.map((val: any) => {
        const result = Number(val.value);
        return !isNaN(result) ? result : (val.value as string);
      });
    });
  results['attributesIds'] = prepareAttributeFilters(filters);

  return results as TransformCommonFiltersForRequestObject;
}

export function prepareAttributeFilters(filters: any): any {
  const result: any = {};
  Object.keys(filters)
    .filter((key: string) => {
      return key.includes('attributes_');
    })
    .forEach((key: string) => {
      const iterationFilters = filters[key];
      result[key.replace('attributes_', '')] = iterationFilters.value.map(
        (val: any) => val.value as unknown
      );
    });
  return result;
}

export function applyFiltersAndGroups(
  widgetRequest: any,
  filter?: any,
  groupBy?: any,
  aggregates: AggregateRequestType[] | null = null
) {
  if (Array.isArray(widgetRequest.data)) {
    widgetRequest.data = widgetRequest.data.map((req: any) => {
      const mutableReq = { ...req };
      mutableReq.filters = [...req.filters];
      mutableReq.groups = [...req.groups];
      mutableReq.aggregates = [...req.aggregates];
      if (aggregates) {
        mutableReq.aggregates = [{ aggregates: aggregates }];
      }
      if (filter != null) {
        mutableReq.filters.push(filter);
      }
      if (groupBy) {
        mutableReq.groups = [
          ...mutableReq.groups,
          {
            member: groupBy,
            sortDirection: 0,
            displayContent: 'Key',
          },
        ];
      }
      return mutableReq as WidgetRequestWithFilters;
    });
  } else if (widgetRequest.data) {
    widgetRequest.data = {
      ...widgetRequest.data,
      filters: [...widgetRequest.data.filters, ...(filter !== null ? [filter] : [])],
      aggregates: !aggregates ? [...widgetRequest.data.aggregates] : [{ aggregates: aggregates }],
      groups: [
        ...(widgetRequest.data.groups ? widgetRequest.data.groups : []),
        ...(groupBy
          ? [
              {
                member: groupBy,
                sortDirection: 0,
                displayContent: 'Key',
              },
            ]
          : []),
      ],
    };
  }

  return widgetRequest.data as WidgetRequestWithFilters;
}

export const OPERATOR_MAP: { [key: string]: number } = {
  lt: 0,
  lte: 1,
  eq: 2,
  neq: 3,
  gte: 4,
  gt: 5,
  startswith: 6,
  endswith: 7,
  contains: 8,
  iscontained: 9, // this has no value in frontend
  doesnotcontain: 10,
  isnull: 11,
  isnotnull: 12,
  isempty: 13,
  isnotempty: 14,
};

export const LOGIC_MAP: { [key: string]: string } = {
  and: '0',
  or: '1',
};

export function normalizeFiltersServerSide(data: CompositeFilterDescriptor): any {
  const newData: any = {
    logicalOperator: LOGIC_MAP[data.logic as string].toString(),
    filterDescriptors: [],
  };
  if (data.filters && data.filters.length > 0) {
    newData.filterDescriptors = [];
    data.filters.forEach((filter: CompositeFilterDescriptor | FilterDescriptor): any => {
      // its composite filter so recursively process it
      // flatten the filters
      if ('logic' in filter) {
        const result = normalizeFiltersServerSide(filter);
        newData.filterDescriptors.push(result);
        return; //continue to the next iteration
      }
      const descriptor = filter;

      const newObj: any = {
        operator: OPERATOR_MAP[descriptor.operator as string].toString(),
        value: normalizeDate(descriptor.value),
        member: descriptor.field,
      };
      newData.filterDescriptors.push(newObj);
    });
  }
  return newData;
}

function normalizeDate(input: any): any {
  if (input instanceof Date) {
    return convertToKwelloDate(input);
  }

  if (Array.isArray(input)) {
    return input.map(normalizeDate);
  }

  return input;
}

export function normalizeSort(sort: SortDescriptor[]) {
  const result = sort.map((sortDescriptor: SortDescriptor) => {
    return {
      member: sortDescriptor.field,
      sortDirection: sortDescriptor.dir == 'asc' ? 0 : 1,
    };
  });
  return result;
}

export interface UseFilterAttributes {
  convertAttributeKeyToDisplayName: (key: string) => string;
  flatFilterAttributes: CustomFilterAttribute[];
}

export function useFilterAttributes() {
  const { activeProject } = useModuleProjectContext();

  const flatFilterAttributes: CustomFilterAttribute[] = useMemo(() => {
    return (
      activeProject?.filters
        ?.map((filter: CustomFilter): CustomFilterAttribute[] => filter.attributes)
        .flat() || []
    );
  }, [activeProject]);

  const convertAttributeKeyToDisplayName = useCallback(
    (key: string) => {
      if (!key) {
        return '';
      }

      const actualKey = getAttributeKey(key);

      const attribute = flatFilterAttributes.find((attr) => attr.attributeName === actualKey);
      return attribute?.attributeDisplayAlias ?? key;
    },
    [flatFilterAttributes]
  );

  return {
    flatFilterAttributes,
    convertAttributeKeyToDisplayName,
  };
}

/**
 * Custom hook that converts chart filters into page filters.
 *
 * @param {string} datasetId - The ID of the dataset for which to select chart filters.
 * @returns {CommonFiltersItem} - An object representing the page filters.
 *
 * This hook uses the `useSelector` hook to retrieve chart filters from the Redux state
 * based on the provided dataset ID. It then uses the `useFilterAttributes` hook to get
 * a function for converting attribute keys to display names.
 *
 * The hook memoizes the transformation of chart filters into page filters using `useMemo`.
 * It iterates over the chart filters and constructs an object where each key is a filter name
 * and the value is an object containing the filter's label, chart filter ID, and an array of values.
 *
 * The filter name is derived from the property name of the chart filter. If the property name
 * contains "Attributes[", it is transformed to a more readable format. The label is generated
 * using the `convertAttributeKeyToDisplayName` function.
 *
 * Dependencies:
 * - `plainChartFilters`: The array of chart filters from the Redux state.
 * - `convertAttributeKeyToDisplayName`: Function to convert attribute keys to display names.
 */
export function useChartFiltersAsPageFilters(datasetId: string) {
  const plainChartFilters: ChartFilterModel[] = useSelector((state: RootState) =>
    selectChartFilters(state, datasetId)
  );
  const { convertAttributeKeyToDisplayName } = useFilterAttributes();

  return useMemo(() => {
    return plainChartFilters.reduce((acc: CommonFiltersItem, filter) => {
      const { propertyName, value, propertyDisplayValue } = filter;

      const valueObject = { name: propertyDisplayValue || value, value };
      const filterName = propertyName.includes('Attributes[')
        ? propertyName.replace("Attributes['", 'attributes_').replace("']", '')
        : propertyName;

      if (acc[filterName]) {
        acc[filterName].value.push(valueObject);
      } else {
        acc[filterName] = {
          label: convertAttributeKeyToDisplayName(filterName),
          chartFilterId: propertyName,
          value: [valueObject],
        };
      }

      return acc;
    }, {});
  }, [plainChartFilters, convertAttributeKeyToDisplayName]);
}

export function useCustomFilters() {
  const { activeProject } = useModuleProjectContext();
  const [filterList, setFilterList] = useState<FilterConfig[]>([]);
  useEffect(() => {
    if (activeProject) {
      const { filters } = activeProject;
      const result: FilterConfig[] = [];
      filters.forEach((filter: CustomFilter) => {
        if (filter.attributes && filter.attributes.length > 0) {
          filter.attributes.forEach((attr: CustomFilterAttribute) => {
            result.push({
              filterAttributeType: filter.attributeType,
              type: FilterType.AUTOCOMPLETE,
              label: attr.attributeDisplayAlias,
              property: 'attributes_' + attr.attributeName,
              claims: [],
              features: [],
            });
          });
        }
      });

      setFilterList(result);
    }
  }, [activeProject]);
  return filterList;
}

export interface GroupOption {
  displayName: string;
  memberName: string;
}

export function widgetGroupOptionsTransformer(
  widgetFilterAttrGroupOptions: string[],
  projectFilters: CustomFilter[]
) {
  const result: GroupOption[] = [];
  const query = projectFilters
    ? projectFilters.filter((value) => widgetFilterAttrGroupOptions.includes(value.attributeType))
    : [];
  if (query.length > 0) {
    const iterator = query.reduce(
      (acc: CustomFilterAttribute[], currentValue: CustomFilter) => [
        ...acc,
        ...currentValue.attributes,
      ],
      []
    );
    iterator.forEach((filterAttr: CustomFilterAttribute) => {
      result.push({
        memberName: "Attributes['" + filterAttr.attributeName + "']",
        displayName: filterAttr.attributeDisplayAlias
          ? filterAttr.attributeDisplayAlias
          : filterAttr.attributeName,
      });
    });
  }
  return result;
}

export type FilterMap = {
  [key in FilterParent]: FilterConfig[];
};

export const FILTER_MAP: FilterMap = {
  [FilterParent.SURVEY]: INSIGHT_SUMMARY_FILTER,
  [FilterParent.COACH]: COACH_FILTER,
  [FilterParent.INTERACTION]: INTERACTION_FILTER,
  [FilterParent.ALIGNMENT]: ALIGNMENT_FILTER,
  [FilterParent.ACTIVITY_RESPONSES]: ALIGNMENT_DETAIL_FILTER,
  [FilterParent.MIR]: MIR_FILTER,
  [FilterParent.UNIVERSAL]: UNIVERSAL_FILTER,
};

export function getAplicableFilters<FilterCollection extends CommonFiltersItem>(
  currentFilterParent: FilterParent,
  currentFilters: FilterCollection
): FilterCollection {
  const currentTemplate = FILTER_MAP[currentFilterParent];
  const newFilterEntity = Object.entries(currentFilters).filter(
    // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
    ([key, value]) => currentTemplate.findIndex((ct) => ct.property === key) !== -1
  );
  return Object.fromEntries(newFilterEntity) as FilterCollection;
}
